/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { Route, Routes } from '@angular/router'

import { authGuard } from './guards/auth.guard'
import { numericIdGuard } from './guards/numeric-id.guard'
import { environment } from 'src/environments/environment'

const pageNotFound = () =>
  import('./modules/sistema/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent)

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./modules/concurso/lista-inscricao-aberta/lista-inscricao-aberta.component').then(
        m => m.ListaInscricaoAbertaComponent
      ),
    data: { title: 'Inscrições Abertas', description: 'Veja os concursos com inscrições abertas na UNICAMP' },
  },
  {
    path: 'concurso/inscricao-aberta',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/concurso/lista-inscricao-aberta/lista-inscricao-aberta.component').then(
        m => m.ListaInscricaoAbertaComponent
      ),
    data: { title: 'Inscrições Abertas', description: 'Veja os concursos com inscrições abertas na UNICAMP' },
  },
  {
    path: 'concurso/inscricao-andamento',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/concurso/lista-inscricao-andamento/lista-inscricao-andamento.component').then(
        m => m.ListaInscricaoAndamentoComponent
      ),
    data: { title: 'Concursos em Andamento', description: 'Acompanhe os concursos em andamento na UNICAMP' },
  },
  {
    path: 'concurso/inscricao-proxima',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/concurso/lista-inscricao-proxima/lista-inscricao-proxima.component').then(
        m => m.ListaInscricaoProximaComponent
      ),
    data: { title: 'Próximos Concursos', description: 'Confira os próximos concursos previstos na UNICAMP' },
  },
  {
    path: 'concurso/inscricao-encerrada',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/concurso/lista-inscricao-encerrada/lista-inscricao-encerrada.component').then(
        m => m.ListaInscricaoEncerradaComponent
      ),
    data: { title: 'Concursos Encerrados', description: 'Consulte os concursos encerrados na UNICAMP' },
  },
  {
    path: 'concurso/:id',
    pathMatch: 'full',
    loadComponent: () => import('./modules/concurso/concurso/concurso.component').then(m => m.ConcursoComponent),
    canActivate: [numericIdGuard],
    data: { title: 'Detalhes do Concurso' },
  },
  {
    path: 'concurso/:id/inscricao',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/concurso/ficha-inscricao/ficha-inscricao.component').then(m => m.FichaInscricaoComponent),
    canActivate: [numericIdGuard],
    data: { title: 'Formulário de Inscrição' },
  },
  {
    path: 'sistema/manual',
    pathMatch: 'full',
    loadComponent: () => import('./modules/sistema/manual/manual.component').then(m => m.ManualComponent),
    data: { title: 'Manual' },
  },
  {
    path: 'sistema/entrar',
    pathMatch: 'full',
    loadComponent: () => import('./modules/sistema/auth/auth.component').then(m => m.AuthComponent),
    data: { title: 'Entrar', noindex: true },
  },
  {
    path: 'sistema/senha-recuperar',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/sistema/senha-recuperar/senha-recuperar.component').then(m => m.SenhaRecuperarComponent),
    data: { title: 'Recuperar Senha', noindex: true },
  },
  {
    path: 'sistema/senha-redefinir/:codigo',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/sistema/senha-redefinir/senha-redefinir.component').then(m => m.SenhaRedefinirComponent),
    data: { title: 'Redefinir Senha', noindex: true },
  },
  {
    path: 'sistema/email-confirmar/:token',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/sistema/email-confirmar/email-confirmar.component').then(m => m.EmailConfirmarComponent),
    data: { title: 'Confirmar E-mail', noindex: true },
  },
  {
    path: 'candidato/inscricoes',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/candidato-inscricoes/candidato-inscricoes.component').then(
        m => m.CandidatoInscricoesComponent
      ),
    canActivate: [authGuard],
    data: { title: 'Minhas Inscrições' },
  },
  {
    path: 'candidato/inscricao/:id',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/candidato-inscricao-ficha/candidato-inscricao-ficha.component').then(
        m => m.CandidatoInscricaoFichaComponent
      ),
    canActivate: [authGuard, numericIdGuard],
    data: { title: 'Ficha de Inscrição' },
  },
  {
    path: 'candidato/inscricao/:id/complemento-fase-1',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/candidato-inscricao-complemento-fase1/candidato-inscricao-complemento-fase1.component').then(
        m => m.CandidatoInscricaoComplementoFase1Component
      ),
    canActivate: [authGuard, numericIdGuard],
    data: { title: 'Documentação Complementar - Fase 1' },
  },
  {
    path: 'candidato/inscricao/:id/complemento-fase-2',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/candidato-inscricao-complemento-fase2/candidato-inscricao-complemento-fase2.component').then(
        m => m.CandidatoInscricaoComplementoFase2Component
      ),
    canActivate: [authGuard, numericIdGuard],
    data: { title: 'Documentação Complementar - Fase 2' },
  },
  {
    path: 'candidato/inscricao/:id/recurso',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/recurso-cadastrar/recurso-cadastrar.component').then(
        m => m.RecursoCadastrarComponent
      ),
    canActivate: [authGuard, numericIdGuard],
    data: { title: 'Cadastrar Recurso' },
  },
  {
    path: 'candidato/recursos',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/recurso-listar/recurso-listar.component').then(m => m.RecursoListarComponent),
    canActivate: [authGuard],
    data: { title: 'Meus Recursos' },
  },
  {
    path: 'candidato/recurso/:id',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/recurso-visualizar/recurso-visualizar.component').then(
        m => m.RecursoVisualizarComponent
      ),
    canActivate: [authGuard, numericIdGuard],
    data: { title: 'Visualizar Recurso' },
  },
  {
    path: 'candidato/senha-alterar',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/senha-alterar/senha-alterar.component').then(m => m.SenhaAlterarComponent),
    canActivate: [authGuard],
    data: { title: 'Alterar Senha', noindex: true },
  },
  {
    path: 'candidato/email-alterar',
    pathMatch: 'full',
    loadComponent: () =>
      import('./modules/candidato/email-alterar/email-alterar.component').then(m => m.EmailAlterarComponent),
    canActivate: [authGuard],
    data: { title: 'Alterar E-mail', noindex: true },
  },
  ...(environment.enableTelaContato === 'true'
    ? ([
        {
          path: 'candidato/contato/:idConcurso',
          pathMatch: 'full',
          loadComponent: () => import('./modules/candidato/contato/contato.component').then(m => m.ContatoComponent),
          canActivate: [authGuard, numericIdGuard],
          data: { title: 'Contato' },
        },
      ] as Route[])
    : []),
  {
    path: 'acessibilidade',
    loadComponent: () => import('./modules/sistema/acessibilidade/acessibilidade.component').then(m => m.AcessibilidadeComponent),
    data: { title: 'Acessibilidade' },
  },
  {
    path: '404',
    loadComponent: pageNotFound,
    data: { title: 'Página não encontrada' },
  },
  {
    path: '**',
    loadComponent: pageNotFound,
    data: { title: 'Página não encontrada' },
  },
]
